import {
  CellClassParams,
  ColDef,
  EditableCallbackParams,
  ICellRendererParams,
} from "ag-grid-community";

import { MIN_COLUMN_WIDTH } from "./gridDefaults";
import {
  CLASSNAMES,
  CellRendererComponent,
  cellEditorSelector,
  getCellClassesRules,
  getCellRendererSelector,
  isEditable,
  valueFormatter,
  valueGetter,
  valueSetter,
} from "./buildColumns";

import type {
  ColumnBuildStrategy,
  LeafColumnBehavior,
  LeafColumnBehaviorBuilder,
} from "./columnBuildStrategy";

// The SBS grid is a read-only comparison surface — required-field highlighting
// (cell--required-missing) is a build-tab concept that does not apply here.
const getSbsCellClassRules = (): Record<
  string,
  (params: CellClassParams) => boolean
> =>
  Object.fromEntries(
    Object.entries(getCellClassesRules()).filter(
      ([key]) => key !== CLASSNAMES.CELL_REQUIRED_MISSING,
    ),
  );

/**
 * Marks spec-driven cell renderers as rendering into the comparison grid, so they
 * can drop build-tab affordances — a renderer that prompts "Click to select" on an
 * empty cell is offering an edit the SBS view isn't asking for, the same reason
 * `getSbsCellClassRules` drops required-field highlighting.
 *
 * Renderers that don't read the flag are unaffected.
 */
const getSbsCellRendererSelector = (
  CellRenderer?: CellRendererComponent,
): ((
  params: ICellRendererParams,
) => ReturnType<ReturnType<typeof getCellRendererSelector>>) => {
  const select = getCellRendererSelector(CellRenderer);

  return (params: ICellRendererParams) => {
    const result = select(params);
    if (!result) return result;

    return { ...result, params: { ...result.params, comparisonView: true } };
  };
};

const EMPTY_FIELD_KEYS: ReadonlySet<string> = new Set<string>();

const isListedField =
  (fieldKeys: ReadonlySet<string>) =>
  (params: EditableCallbackParams): boolean => {
    const benefitKey = (params.data as { benefitKey?: string } | undefined)
      ?.benefitKey;
    if (!benefitKey) return false;
    return fieldKeys.has(benefitKey) || fieldKeys.has(benefitKey.split(".")[0]);
  };

export class SbsLeafColumnBehaviorBuilder implements LeafColumnBehaviorBuilder {
  private readonly params: Record<string, unknown> = {};
  private readonly strategy: ColumnBuildStrategy;

  constructor(strategy: ColumnBuildStrategy) {
    this.strategy = strategy;
  }

  addParam(key: string, value: unknown): this {
    this.params[key] = value;
    return this;
  }

  build(): LeafColumnBehavior {
    if (this.strategy instanceof SbsComparisonStrategy) {
      return this.strategy.getLeafColumnBehavior(
        this.params as Partial<{ isCurrentCarrier: boolean }>,
      );
    }

    return {};
  }
}

/**
 * Produces read-only leaf columns from the same BPG data.
 */
export class SbsComparisonStrategy implements ColumnBuildStrategy {
  private readonly CellRenderer?: CellRendererComponent;
  private readonly editableNonCurrentCarriers: boolean;
  private readonly editableCurrentCarrierFieldKeys: ReadonlySet<string>;

  constructor(
    CellRenderer?: CellRendererComponent,
    options: {
      editableNonCurrentCarriers?: boolean;
      editableCurrentCarrierFieldKeys?: ReadonlySet<string>;
    } = {},
  ) {
    this.CellRenderer = CellRenderer;
    this.editableNonCurrentCarriers =
      options.editableNonCurrentCarriers ?? false;
    this.editableCurrentCarrierFieldKeys =
      options.editableCurrentCarrierFieldKeys ?? EMPTY_FIELD_KEYS;
  }

  getLeafColumnBehaviorBuilder(): SbsLeafColumnBehaviorBuilder {
    return new SbsLeafColumnBehaviorBuilder(this);
  }

  getLeafColumnBehavior(
    params: Partial<{ isCurrentCarrier: boolean }> = {},
  ): LeafColumnBehavior {
    const { isCurrentCarrier } = params;
    const { CellRenderer } = this;
    // Only enable edit when isCurrentCarrier is explicitly false; treat
    // missing/undefined values as non-editable so callers that omit the
    // param can't unintentionally flip columns into edit mode.
    const allowEdit =
      this.editableNonCurrentCarriers && isCurrentCarrier === false;

    // The current carrier — PPX's Incumbent/Requested column — stays read-only
    // except for the field keys the caller names, which are the extended
    // (non-v5) fields analysts enter on the RFP-requested plan (CD-2301).
    const currentCarrierFieldKeys = this.editableCurrentCarrierFieldKeys;
    const allowCurrentCarrierEdit =
      isCurrentCarrier === true && currentCarrierFieldKeys.size > 0;
    const isFieldListed = isListedField(currentCarrierFieldKeys);
    const editable = allowEdit
      ? isEditable
      : allowCurrentCarrierEdit
        ? (params: EditableCallbackParams) =>
            isFieldListed(params) && isEditable(params)
        : false;
    const editingEnabled = allowEdit || allowCurrentCarrierEdit;

    const leafBehavior: LeafColumnBehavior = {
      editable,
      cellEditorSelector: editingEnabled ? cellEditorSelector : undefined,
      cellRendererSelector: getSbsCellRendererSelector(CellRenderer),
      valueSetter: editingEnabled ? valueSetter : undefined,
      cellClassRules: getSbsCellClassRules(),
      valueFormatter,
      valueGetter,
      minWidth: MIN_COLUMN_WIDTH,
    };

    return leafBehavior;
  }

  getDefaultColDefOverrides(): Partial<ColDef> {
    return {
      // MPX benefit popovers open on double-click, not a single click.
      singleClickEdit: false,
      editable: false,
      // Column widths are controlled solely by the narrow/medium/wide preset,
      // not manual dragging.
      resizable: false,
      wrapText: true,
    };
  }
}
